import { combineLatest, Observable, take } from 'rxjs';
import { Store } from '@ngrx/store';

import {
  CommonBookingFeatureState,
  SeatMapActions,
  ServicesActions,
  serviceSelectionsForFragment,
} from '@fcom/common-booking';
import { ModifiedServiceInfo, SelectionsPerTraveler, TravelerService } from '@fcom/dapi/interfaces';
import { Category, FinnairOrder, FinnairServiceRequestItem } from '@fcom/dapi/api/models';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { isBoundBasedCategory } from '@fcom/common-booking/modules/ancillaries/utils/category.utils';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { unique } from '@fcom/core/utils';

import { ManageBookingService } from '../services';

/**
 * Remove seat product from cart
 */
export function removeSeatProduct(
  serviceInfo: ModifiedServiceInfo,
  store$: Store<CommonBookingFeatureState>,
  manageBookingService: ManageBookingService,
  order$: Observable<FinnairOrder>,
  callbacks?: { onSuccess?: () => void; onError?: () => void }
): void {
  order$.pipe(take(1)).subscribe((order: FinnairOrder) => {
    store$.dispatch(
      SeatMapActions.setSeatForFlight({
        flightId: serviceInfo.fragmentId,
        travelerId: serviceInfo.travelerId,
        seat: {},
      })
    );
    removeService(order, serviceInfo, store$, manageBookingService, callbacks);
  });
}

/**
 * Remove non-seat product from cart
 */
export function removeNonSeatProduct(
  serviceInfo: ModifiedServiceInfo,
  store$: Store<CommonBookingFeatureState>,
  manageBookingService: ManageBookingService,
  order$: Observable<FinnairOrder>,
  callbacks?: { onSuccess?: () => void; onError?: () => void }
): void {
  const fragmentId = isBoundBasedCategory(serviceInfo.category) ? serviceInfo.boundId : serviceInfo.fragmentId;

  combineLatest([
    store$.pipe(serviceSelectionsForFragment<TravelerService[]>(fragmentId, serviceInfo.category)),
    order$,
  ])
    .pipe(take(1))
    .subscribe((values: [SelectionsPerTraveler<TravelerService[]>, FinnairOrder]) => {
      const [storeSelections, order] = values;
      store$.dispatch(
        ServicesActions.setSelectionsForFragment({
          category: serviceInfo.category,
          fragmentId: fragmentId,
          selections: createNewSelections(serviceInfo, storeSelections),
        })
      );
      removeService(order, serviceInfo, store$, manageBookingService, callbacks);
    });
}

/**
 * Remove seat and non seat products from cart
 */
export function removeAllProducts(
  store$: Store<CommonBookingFeatureState>,
  manageBookingService: ManageBookingService,
  order: FinnairOrder
): Observable<FinnairOrder> {
  const unpaidSeats = order.services.unpaid.find((service) => service.category === Category.SEAT);
  const unpaidServices = order.services.unpaid.find((service) => service.category !== Category.SEAT);
  if (unpaidSeats) {
    store$.dispatch(
      SeatMapActions.clearAllSeats({
        travelerIds: unpaidSeats.bounds
          .flatMap((bound) => bound.segments)
          .flatMap((segment) => segment.passengers.flatMap((passenger) => passenger.id))
          .filter(unique),
      })
    );
  }
  if (unpaidServices) {
    store$.dispatch(ServicesActions.clearAllCategories());
  }
  return removeAllServices(order, store$, manageBookingService);
}

export function createNewSelections(
  serviceInfo: ModifiedServiceInfo,
  storeSelections: SelectionsPerTraveler<TravelerService[]>
): SelectionsPerTraveler<TravelerService[]> {
  if (storeSelections[serviceInfo.travelerId]?.length) {
    const filtered = storeSelections[serviceInfo.travelerId].filter(
      (selection) => selection.variant !== serviceInfo.variant
    );
    if (filtered.length === 0) {
      return excludeTravelerId(storeSelections, serviceInfo.travelerId);
    }

    return {
      ...storeSelections,
      [serviceInfo.travelerId]: filtered,
    };
  }
  return excludeTravelerId(storeSelections, serviceInfo.travelerId);
}

function excludeTravelerId(
  storeSelections: SelectionsPerTraveler<TravelerService[]>,
  travelerId: string
): SelectionsPerTraveler<TravelerService[]> {
  const newSelections = {} as SelectionsPerTraveler<TravelerService[]>;
  for (const key of Object.keys(storeSelections)) {
    if (key !== travelerId) {
      newSelections[key] = storeSelections[key];
    }
  }
  return newSelections;
}

export function removeService(
  order: FinnairOrder,
  serviceInfo: ModifiedServiceInfo,
  store$: Store<CommonBookingFeatureState>,
  manageBookingService: ManageBookingService,
  callbacks?: { onSuccess?: () => void; onError?: () => void }
): void {
  const allFlights = order.bounds.flatMap((b) => b.itinerary).filter(isFlight);
  const [requests] = getServiceData(store$, serviceInfo.category, order.bounds, allFlights);

  let filteredRequests = requests;

  if (serviceInfo.seatNumber) {
    filteredRequests = requests
      .map((request) => {
        if (request.fragmentId !== serviceInfo.fragmentId) {
          return null;
        }

        return {
          ...request,
          serviceSelection: request.serviceSelection.filter((serviceSelection) => {
            return serviceSelection.travelerId === serviceInfo.travelerId;
          }),
        };
      })
      .filter(Boolean) as FinnairServiceRequestItem[];
  }

  manageBookingService.setServices(filteredRequests, [serviceInfo.category], {
    next: () => callbacks?.onSuccess?.(),
    error: () => callbacks?.onError?.(),
  });
}

export function removeAllServices(
  order: FinnairOrder,
  store$: Store<CommonBookingFeatureState>,
  manageBookingService: ManageBookingService
): Observable<FinnairOrder> {
  const allFlights = order.bounds.flatMap((b) => b.itinerary).filter(isFlight);
  const removeServicesRequests = order.services.unpaid
    .map((service) => service.category)
    .flatMap((category) => {
      const [requests] = getServiceData(store$, category, order.bounds, allFlights);
      return requests;
    });
  return manageBookingService.removeAllServices(removeServicesRequests, order.id, order.hash);
}
